// What a signed-in member's action record may contain (analytics round 5, 2026-10-17).
// Shared by the browser helper (memberActivity.ts), which trims what it sends to these limits,
// and the server (memberActivityServer.ts + POST /api/member-activity), which rejects anything
// outside them. The limits and the action list mirror public.member_activity and
// record_member_activity() in supabase/migrations/20261017030000_member_activity.sql.

export const MEMBER_ACTIONS = [
  'search',
  'sheet_view',
  'sheet_download',
  'vendor_contact',
  'save',
  'unsave',
  'question',
] as const

export type MemberAction = (typeof MEMBER_ACTIONS)[number]

export const MEMBER_ACTIVITY_LIMITS = { path: 300, target: 300, searchText: 200 } as const

export interface MemberActivityInput {
  action: MemberAction
  /** Site path the action happened on, without query string or fragment. */
  path?: string
  /** Sheet, vendor, saved page or form. Never a person. */
  target?: string
  /** Searches only. */
  searchText?: string
}

export function isMemberAction(value: unknown): value is MemberAction {
  return typeof value === 'string' && (MEMBER_ACTIONS as readonly string[]).includes(value)
}

/** Path only: query string and fragment cut, trimmed; undefined when empty. */
export function stripPath(value: string): string | undefined {
  const p = (value.trim().split(/[?#]/, 1)[0] ?? '').trim()
  return p || undefined
}

/** A same-site path: starts with one '/' (a '//' prefix would be another host). */
export function isSitePath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//')
}

/**
 * Strict server-side check of a request body. Returns the cleaned record, or null when the
 * action is unknown or any field has the wrong type, is not a site path, or is too long.
 * The search text is kept for 'search' only and dropped for every other action.
 */
export function parseMemberActivity(body: unknown): MemberActivityInput | null {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null
  const { action, path, target, searchText } = body as Record<string, unknown>
  if (!isMemberAction(action)) return null

  const out: MemberActivityInput = { action }

  if (path !== undefined && path !== null) {
    if (typeof path !== 'string') return null
    const p = stripPath(path)
    if (p !== undefined) {
      if (!isSitePath(p) || p.length > MEMBER_ACTIVITY_LIMITS.path) return null
      out.path = p
    }
  }

  if (target !== undefined && target !== null) {
    if (typeof target !== 'string') return null
    const t = target.trim()
    if (t.length > MEMBER_ACTIVITY_LIMITS.target) return null
    if (t) out.target = t
  }

  if (action === 'search' && searchText !== undefined && searchText !== null) {
    if (typeof searchText !== 'string') return null
    const s = searchText.trim()
    if (s.length > MEMBER_ACTIVITY_LIMITS.searchText) return null
    if (s) out.searchText = s
  }

  return out
}
